<script setup lang="ts">
import type { OutputBlockData } from '@editorjs/editorjs';
import ArticleParserBlockquote from './ArticleParserBlockquote.vue';
import ArticleImage from '../ArticleImage.vue';
import ArticleRelatedNews from '../ArticleRelatedNews.vue';
import { CLIENT_NEWS_CATEGORY_URL } from '~/shared/api/client/news/news.const';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';
import { resizeHtmlImages } from '~/shared/lib/resizeHtmlImages';

const props = defineProps<{
  blocks: OutputBlockData[];
}>();

const getBlockImageUrl = (data: OutputBlockData['data']): string =>
  data?.photo?.photo?.file_url ||
  data?.photo?.file_url ||
  data?.file?.url ||
  data?.url ||
  '';
</script>

<template>
  <template v-for="block in props.blocks">
    <ArticleParserBlockquote
      v-if="block.type === 'quote'"
      :key="`blockquote-${block.id}`"
      :name="block.data?.person?.fio || ''"
      :description="block.data?.person?.position || ''"
      :text="block.data?.data || ''"
      :photo="block.data?.person?.photo?.file_url || ''"
    />
    <ArticleImage
      v-else-if="block.type === 'photo'"
      :key="`article-image-${block.id}`"
      :image="getBlockImageUrl(block.data)"
      :source="block.data?.source || ''"
    />
    <img
      v-else-if="block.type === 'image'"
      :key="`image-${block.id}`"
      :src="resizeImage(getBlockImageUrl(block.data), { width: 860 })"
      :srcset="
        resizeImageSrcsetWidths(
          getBlockImageUrl(block.data),
          [480, 720, 860, 1280, 1720, 2580],
        ) || undefined
      "
      sizes="(max-width: 900px) 100vw, 860px"
      :alt="block.data?.caption || ''"
    />
    <div
      v-else-if="block.type === 'idea'"
      :key="`idea-${block.id}`"
      class="key-point"
    >
      {{ block.data?.text }}
    </div>
    <ArticleRelatedNews
      v-else-if="block.type === 'related'"
      :key="`related-${block.id}`"
      :title="block.data?.item?.title || ''"
      :subject="block.data?.item?.subject || ''"
      :category="block.data?.item?.category.name || ''"
      :category-link="`${CLIENT_NEWS_CATEGORY_URL}${block.data?.item?.category.id || ''}`"
      :link="`/${block.data?.item.type}/${block.data?.item.id || ''}`"
      :image="block.data?.item?.image?.photo?.file_url || ''"
    />
    <h2
      v-else-if="block.type === 'header' && block.data?.level === 2"
      :key="`header2-${block.id}`"
      v-html="block.data?.text"
    />
    <h3
      v-else-if="block.type === 'header' && block.data?.level === 3"
      :key="`header3-${block.id}`"
      v-html="block.data?.text"
    />
    <h4
      v-else-if="block.type === 'header' && block.data?.level === 4"
      :key="`header4-${block.id}`"
      v-html="block.data?.text"
    />
    <p
      v-else-if="block.type === 'paragraph'"
      :key="`paragraph-${block.id}`"
      v-html="block.data?.text"
    />
    <div
      v-else-if="block.type === 'raw'"
      :key="`raw-${block.id}`"
      v-html="resizeHtmlImages(block.data?.html || '')"
    />
    <ul
      v-else-if="block.type === 'list' && block.data?.style === 'unordered'"
      :key="`list-unordered-${block.id}`"
    >
      <li
        v-for="(item, index) in block.data?.items"
        :key="`list-unordered-item-${index}`"
        v-html="item.content"
      />
    </ul>
    <ol
      v-else-if="block.type === 'list' && block.data?.style === 'ordered'"
      :key="`list-ordered-${block.id}`"
    >
      <li
        v-for="(item, index) in block.data?.items"
        :key="`list-ordered-item-${index}`"
        v-html="item.content"
      />
    </ol>
  </template>
</template>
